/* eslint-disable no-useless-escape */
import type { TagEntity } from '@schibsted/frapi-schema';
import type { FrapiTeaserItem } from '../../../types/frapi-types.js';
import type { SortConfig } from '../../layouts/front/sectionHelpers.js';

export type FilterComponent = 'TagCloud' | 'Dropdown' | 'Checkbox';

export type FilterRelationship = 'AND' | 'OR';

export interface FilterMapping {
  relationship: FilterRelationship;
  component: FilterComponent;
  title: string;
  order: number;
}

export interface FilterMappings {
  [key: string]: {
    [key: string]: FilterMapping;
  };
}

export const checkboxOrder = ['Lav', 'Middels', 'Høy'];

export const omraadeOrder = ['Bergen', 'Trondheim'];

export const sortByPriority =
  (order: string[]) => (a: TagEntity, b: TagEntity) => {
    const rank = (tag: TagEntity) => {
      const index = order.indexOf(formatTag(tag.title));

      return index === -1 ? order.length : index;
    };

    return rank(a) - rank(b);
  };

export const filterMappings: FilterMappings = {
  'mat-og-drikke': {
    område: {
      relationship: 'OR',
      component: 'TagCloud',
      title: 'Områder',
      order: 1,
    },
    kjøkken: {
      relationship: 'OR',
      component: 'TagCloud',
      title: 'Kjøkken',
      order: 2,
    },
    mattype: {
      relationship: 'OR',
      component: 'TagCloud',
      title: 'Type mat',
      order: 3,
    },
    hererdet: {
      relationship: 'AND',
      component: 'TagCloud',
      title: 'Her er det',
      order: 4,
    },
    pris: {
      relationship: 'OR',
      component: 'Checkbox',
      title: 'Prisnivå',
      order: 5,
    },
  },
  'film-og-serier': {
    type: {
      relationship: 'OR',
      component: 'TagCloud',
      title: 'Sjanger',
      order: 1,
    },
    strømmetjeneste: {
      relationship: 'OR',
      component: 'TagCloud',
      title: 'Strømmetjeneste',
      order: 2,
    },
    format: {
      relationship: 'AND',
      component: 'TagCloud',
      title: 'Format',
      order: 3,
    },
  },
};

export const formatTag = (title: string) => title.split('/')[2];

export const getCategoryTitle = (tag: TagEntity) => tag.title.split('/')[1];

export const reduceFilters = (
  filters: TagEntity[],
): Record<string, TagEntity[]> =>
  filters.reduce((acc: Record<string, TagEntity[]>, tag) => {
    const categoryTitle = getCategoryTitle(tag);
    acc[categoryTitle] = [...(acc[categoryTitle] || []), tag];

    return acc;
  }, {});

export const generateSearchQuery = (
  activeFilters: TagEntity[],
  section: string | undefined,
): string => {
  const reducedFilters = reduceFilters(activeFilters);
  const search = new URLSearchParams();

  Object.entries(reducedFilters).forEach(([key, filters]) => {
    const relationship = section && filterMappings[section][key].relationship;

    if (relationship === 'AND') {
      filters.forEach((f) => {
        search.append('tagIds', f.id);
      });
    } else {
      search.append('tagIds', filters.map((f) => f.id).join('|'));
    }
  });

  const str = search.toString();

  return str.length > 0 ? `&${str}` : '';
};

export const generateTagQuery = (
  activeFilters: TagEntity[],
  section: string,
) => {
  const reducedFilters = reduceFilters(activeFilters);

  let str = '';
  Object.entries(reducedFilters).forEach(([key, filters]) => {
    const relationship = filterMappings[section][key].relationship;

    str +=
      (str ? ',' : '') +
      filters.map((f) => f.id).join(relationship === 'AND' ? ',' : '|');
  });

  return encodeURIComponent(str);
};

export const replacedTagUrl = (address: string, id: string) =>
  address
    .replace(id, '')
    .replace(',|', ',')
    .replace(',,', ',')
    .replace('/,', '/')
    .replace('&tagIds=|', '&tagIds=')
    .replace('&tagIds=&', '&')
    .replace(',?sorting', '?sorting')
    .replace('&tagId=?sorting', '?sorting')
    .replace('/|', '/')
    .replace('|&', '&')
    .replace('|,', ',')
    .replace('||', '|')
    .replace(/\|$/, '') //eol
    .replace(/&tagIds=$/, '') //eol
    .replace(/,$/, ''); //eol

export const orderTeaserTags = (teaserTags: TagEntity[]) =>
  teaserTags
    .reduce((acc: TagEntity[], cur) => {
      if (
        !acc.find(
          (item: TagEntity) => getCategoryTitle(item) === getCategoryTitle(cur),
        )
      ) {
        acc.push(cur);
      }

      return acc;
    }, []) // rreturns only the first occurrence of a type
    .sort((a) => (getCategoryTitle(a) === 'type' ? -1 : 1)); // sorts for 'type' first

export const getSorter = (currentUrl: string): SortConfig =>
  currentUrl.match(/sorting=([^&]*)/)?.[1] as SortConfig;

export const removeDuplicates = (articles: FrapiTeaserItem[]) =>
  Object.values(
    articles.reduce((acc: Record<string, FrapiTeaserItem>, current) => {
      if (current.kicker) {
        if (acc[current.kicker]) {
          // If the kicker exists, compare the dates of the current and existing object
          if (current.publicationDate > acc[current.kicker].publicationDate) {
            // If the current object is newer, replace the existing object with the current
            acc[current.kicker] = current;
          }
        } else {
          // If it doesn't exist, add the current object to the accumulator
          acc[current.kicker] = current;
        }
      }

      return acc;
    }, {}),
  );
